import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { validatePassword } from "@/lib/passwordPolicy";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import logo from "@/assets/privy-logo.png";
import { PageMeta } from "@/components/PageMeta";

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [signUpPassword, setSignUpPassword] = useState("");
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, role, mustUpdatePassword } = useAuth();

  // Permanent invite links (/auth?signup=1) open straight on the Sign Up tab
  const defaultTab = (() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("signup") === "1" || p.get("tab") === "signup" ? "signup" : "signin";
  })();

  // Redirect if already logged in (respect ?redirect= param)
  useEffect(() => {
    if (!user) return;
    if (mustUpdatePassword) {
      navigate("/force-password-update", { replace: true });
      return;
    }
    if (role) {
      const params = new URLSearchParams(window.location.search);
      const redirectTo = params.get("redirect");
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
        return;
      }
      if (role === "agent") navigate("/agent-dashboard", { replace: true });
      else if (role === "client" || role === "co_buyer") navigate("/client-dashboard", { replace: true });
      else if (role === "admin") navigate("/admin", { replace: true });
      else if (role === "mls_admin") navigate("/mls-admin", { replace: true });
      return;
    }
    // Logged in but no role assigned — fall back so the user isn't stuck
    // on the login screen. Give the role fetch a moment, then redirect.
    const t = setTimeout(() => {
      toast({
        title: "Account needs setup",
        description: "Your account doesn't have a role assigned. Please contact an admin or use an invite link.",
        variant: "destructive",
      });
      navigate("/", { replace: true });
    }, 1500);
    return () => clearTimeout(t);
  }, [user, role, mustUpdatePassword, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent, type: "signin" | "signup") => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const fullName = formData.get("fullName") as string;

    try {
      if (type === "signup") {
        const pwError = validatePassword(password);
        if (pwError) {
          toast({ title: "Password requirements", description: pwError, variant: "destructive" });
          setLoading(false);
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              role: "client",
            },
            emailRedirectTo: `${window.location.origin}/`,
          },
        });

        if (error) throw error;

        if (data.user) {
          toast({
            title: "Account created!",
            description: data.session
              ? "You're all set — browse the listings."
              : "Please check your email to verify your account.",
          });

          if (data.session) {
            navigate("/client-dashboard");
          }
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        toast({
          title: "Welcome back!",
          description: "You've successfully signed in",
        });

        // Auth context will handle role-based redirect via the useEffect above
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <PageMeta
        title="Agent Login & Signup | Privy Listing Network"
        description="Sign in to Privy Listing Network or create an account to access private, off-market real estate listings shared between agents and their buyers."
        path="/auth"
      />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src={logo} alt="Privy Listing Network" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-semibold leading-none tracking-tight">
            Sign in to Privy Listing Network
          </h1>

          <CardDescription>Sign in or create an agent account to get started</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            <TabsContent value="signin">
              <form onSubmit={(e) => handleSubmit(e, "signin")}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input id="signin-email" name="email" type="email" placeholder="agent@example.com" required />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="signin-password">Password</Label>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate("/forgot-password")}
                      >
                        Forgot password?
                      </Button>
                    </div>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        name="password"
                        type={showSignInPassword ? "text" : "password"}
                        className="pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignInPassword((v) => !v)}
                        aria-label={showSignInPassword ? "Hide password" : "Show password"}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground h-10 w-10 flex items-center justify-center rounded-md"
                      >
                        {showSignInPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </div>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={(e) => handleSubmit(e, "signup")}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input id="signup-name" name="fullName" placeholder="John Smith" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input id="signup-email" name="email" type="email" placeholder="john@example.com" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        name="password"
                        type={showSignUpPassword ? "text" : "password"}
                        className="pr-10"
                        value={signUpPassword}
                        onChange={(e) => setSignUpPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignUpPassword((v) => !v)}
                        aria-label={showSignUpPassword ? "Hide password" : "Show password"}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground h-10 w-10 flex items-center justify-center rounded-md"
                      >
                        {showSignUpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <PasswordRequirements password={signUpPassword} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    New accounts are buyer accounts. Need agent access? Ask an admin to upgrade your account.
                  </p>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>

                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
